import { SPEC_ID_REGEX } from '@features/scoreSpec/specSchema';

// Which score spec published the feed you are looking at.
//
// Publishing a spec yields `score_spec_<id>` for a single-score spec, and
// `score_spec_<id>__<score_name>` once for each score of a multi-score one. So the feed id carries
// its spec id, and no request is needed to work out which spec to ask for.
//
// It does NOT carry it for a combined feed. `fundamental_earnings_screen_v1` has no spec of its
// own — its members each have one — so there is no single prompt to show and this returns null.
// Drilling into a member reaches the member's own feed, which does resolve.
//
// Nor does it carry it for a FREE-FORM name. Since PR #459 a score may set `scores[].feed_name` and
// publish as `score_spec_<feed_name>`, with no spec id in it at all — that is the point of the
// field, since it lets a v4 re-cut keep publishing to v3's feed. Such an id resolves to null here,
// so the prompt panel is absent rather than wrong: the name deliberately no longer says which spec
// (or which VERSION) produced the rows, and there is nothing in the id left to guess from. Resolving
// it would mean a lookup over every spec's spec_json, which is a separate piece of work.
//
// FAILS CLOSED. The remainder after stripping has to match the loader's own id pattern, and
// anything else returns null rather than a guess. Two reasons that matters here: the naming rule
// comes from a document that has already been caught disagreeing with the engine, and a wrong spec
// id would show a confident, wrong prompt beside real scores — worse than showing nothing.
//
// TO VERIFY the rule against reality, from a logged-in session with the feed_view grant:
//
//   const { listFeeds } = await import('/src/services/feeds.js');
//   const { specIdForFeed } = await import('/src/features/feedView/specForFeed.js');
//   (await listFeeds()).feeds.forEach((id) => console.log(id, '->', specIdForFeed(id)));
//
// Every `score_spec_*` id should map to something, and nothing else should. Until that has been
// run, an absent panel and a wrong naming rule look identical.

const FEED_PREFIX = 'score_spec_';

// A published score name, which is the suffix a multi-score spec appends. Same charset the loader
// enforces on `scores[].name`.
const SCORE_SUFFIX = /__[a-z0-9_]+$/;

// The spec id a feed was published from, or null when the feed is not one spec's output.
export const specIdForFeed = (feedId) => {
  const id = String(feedId || '');
  if (!id.startsWith(FEED_PREFIX)) return null;

  const body = id.slice(FEED_PREFIX.length);
  if (!body) return null;

  // A single-score feed keeps the bare spec id, so try that before assuming a score suffix — the
  // suffix pattern would otherwise eat part of an id that legitimately ends in underscores.
  if (SPEC_ID_REGEX.test(body)) return body;

  const withoutScore = body.replace(SCORE_SUFFIX, '');
  if (withoutScore !== body && SPEC_ID_REGEX.test(withoutScore)) return withoutScore;

  return null;
};

// The score this particular feed publishes, when it is one of several from the same spec. Null for
// a single-score feed, whose feed name is bare. Useful for saying which number the rows carry when
// the prompt is shared across siblings.
export const scoreNameForFeed = (feedId) => {
  const specId = specIdForFeed(feedId);
  if (!specId) return null;
  const body = String(feedId).slice(FEED_PREFIX.length);
  if (body === specId) return null;
  return body.slice(specId.length + 2) || null;
};

export const isSpecFeed = (feedId) => specIdForFeed(feedId) !== null;
